/**
 * step.js - how many times a child is asked, and from where.
 *
 * PURE. PLATFORM SCAFFOLD.
 *
 * A STEP is one thing the child is asked for. A TRIAL is one go at it. A step
 * gets several trials, and the rules for how many, and for where each one
 * opens, are contract rules rather than game rules - they were written twice
 * and were subtly different both times.
 *
 * ---------------------------------------------------------------------------
 * TWO CEILINGS, BECAUSE THEY GUARD DIFFERENT THINGS
 *
 *   attemptNumber   what the CHART will see. Section 7.4 colours a box from
 *                   first attempts, so an attempt the recogniser could not hear
 *                   must not consume attempt 1 - the step would then contribute
 *                   nothing at all, because attempts 2 and 3 never count either
 *                   (GAME-01.md section 7.8). It only advances on a result that
 *                   was heard well enough to score.
 *   trials          every go, including the inconclusive ones. Without it a
 *                   noisy room - where nothing is ever heard well enough to
 *                   raise attemptNumber - leaves a child stuck on one step
 *                   forever.
 *
 * When both are spent, the step is over. What happens then is the game's:
 * doing it together is the usual answer, and it is NOT recorded as an attempt.
 * Nothing was measured, and writing a failure there would punish the child for
 * the game running out of patience.
 *
 * ---------------------------------------------------------------------------
 * WHERE A RETRY OPENS
 *
 * Where the last trial ENDED UP, not back at the top. A child who has just
 * needed the model should not be asked cold again - section 2.4: support comes
 * before failure, and it does not evaporate between two goes at the same word.
 */

import { clampRung, moreSupport, rungIndexOf } from './support.js';

/** Trials the chart will see before the game does it with the child. */
export const MAX_ATTEMPTS_PER_STEP = 3;

/**
 * @param {object} input
 * @param {number} input.phase
 * @param {number} input.openingIndex   the level's current rung
 * @param {number} [input.maxAttempts]
 * @param {number} [input.maxTrials]    defaults to maxAttempts + 2
 * @returns {object} step state
 */
export function createStep({
  phase,
  openingIndex,
  maxAttempts = MAX_ATTEMPTS_PER_STEP,
  maxTrials = maxAttempts + 2,
}) {
  let attemptNumber = 1;
  let trials = 0;
  let rung = clampRung(phase, openingIndex);

  return {
    attemptNumber: () => attemptNumber,
    trials: () => trials,
    rung: () => rung,

    /** A trial is about to open. Returns the rung it opens at. */
    open() {
      trials += 1;
      return rung;
    },

    /**
     * The trial did not succeed.
     *
     * @param {{record:object|null, heardWell:{reliable:boolean}}} result
     * @returns {boolean} is there another go left?
     */
    missed({ record, heardWell }) {
      if (heardWell && heardWell.reliable) attemptNumber += 1;
      rung = record && record.supportRung
        ? clampRung(phase, rungIndexOf(record.supportRung))
        : moreSupport(phase, rung);
      return attemptNumber <= maxAttempts && trials < maxTrials;
    },

    /**
     * The engine raised support between trials. Section 2.4: that takes effect
     * on the very next trial, including a retry of the step in progress.
     *
     * A FADE deliberately does not come through here. Making a trial harder
     * half way through a step - which is what a late server recommendation
     * would do - is not what earning independence should feel like; it takes
     * effect at the next step.
     */
    supportRaised(index) {
      rung = clampRung(phase, index);
    },
  };
}

export default { createStep, MAX_ATTEMPTS_PER_STEP };
